import { useState } from 'react';
import { FormField, Input } from '@amzn/awsui-components-core';
import ConfirmationModalView from '../ConfirmationModalView';
import { isValidSubsectionId } from '../InlineValidations';

export interface SubsectionIdModalProps {
  /** Closes the modal without saving. */
  readonly onDismiss: () => void;
  /** Called with the edited subsection ID when the user saves. */
  readonly onSave: (value: string) => void;
  /** Value to seed the input with. */
  readonly initialValue?: string;
}

/**
 * Modal for editing a heading's subsection ID.
 * The ID becomes the heading's HTML `id`, enabling `#fragment` anchor links.
 * Mounted only while open, so the input re-seeds on each open.
 */
export function SubsectionIdModal({
  onDismiss,
  onSave,
  initialValue = '',
}: SubsectionIdModalProps) {
  const [value, setValue] = useState(initialValue);

  const isValid = isValidSubsectionId(value);

  return (
    <ConfirmationModalView
      header="Edit Subsection ID"
      confirmLabel="Save"
      isConfirming={false}
      isConfirmDisabled={!isValid}
      onDismiss={onDismiss}
      onConfirm={() => onSave(value)}
      body={
        <FormField
          label="Subsection ID"
          description="Set a custom ID for this heading. This enables #fragment anchor links (e.g. #my-section-id) to link directly to this heading."
          errorText={
            isValid ? undefined : 'Use only letters, numbers, hyphens, and underscores — no spaces.'
          }
          stretch
        >
          <Input
            value={value}
            onChange={({ detail }) => setValue(detail.value)}
            placeholder="e.g. my-section-id"
            autoFocus
          />
        </FormField>
      }
    />
  );
}
